/**
 * The one risk scale's words (D270): what a level means and how a person reads it,
 * "Risk level 4 out of 5: needs approval — this deletes a branch.", and that a 4–5 waits for an owner.
 *
 * The level's words, `defaultRiskLevel` and `riskLevelSentence` are AIOE's (v0.40.0,
 * `shared/schemas/tools.ts`), restated here because that file pulls in node:crypto and the
 * renderer reads this one; src/test/riskScale.test.ts holds the two equal. The schemas and the
 * rest of the contract are in shared/schemas/riskScale.ts.
 *
 *   1–2  proceed: no prompt beyond what the permission mode already asks
 *   3    proceeds, with a visible warning
 *   4–5  halt for a person's approval, and that person must be an owner of the project
 *
 * Plain constants and functions only, no imports: the host, the desktop and the renderer's
 * tests all read it.
 */
export const RISK_LEVELS = [1, 2, 3, 4, 5];
export const RISK_LEVEL_WORDS = { 1: "Routine", 2: "Low", 3: "Needs a look", 4: "Needs approval", 5: "Halts until approved" };
/** The level a class implies when nobody set one: reads 1, changes 3, destroys 5. */
export const defaultRiskLevel = (risk) => (risk === "reads" ? 1 : risk === "changes" ? 3 : 5);
/** "Risk level 4 out of 5: needs approval". */
export const riskLevelSentence = (level) => {
    const w = RISK_LEVEL_WORDS[level] ?? "";
    return `Risk level ${level} out of 5${w ? `: ${w.charAt(0).toLowerCase()}${w.slice(1)}` : ""}`;
};
/** The longest `riskWords` an Inbox record carries (AIOE's notifications, v0.40.0). */
export const RISK_WORDS_MAX = 100;
export function riskDisposition(level) {
    return level >= 4 ? "halt" : level === 3 ? "warn" : "proceed";
}
/** Levels 4 and 5 are approved by an owner of the project, whoever the request is addressed to. */
export function ownerMustApprove(level) {
    return typeof level === "number" && level >= 4;
}
/** What every surface says on a level 4–5 request, and what a non-owner's approval is refused with. */
export const OWNER_REQUIRED_SENTENCE = "An owner must approve this.";
/** The error code for that refusal (403 on the hub and the organisation). */
export const OWNER_REQUIRED_ERROR = "owner_required";
/** An answer that refuses rather than approves: a non-owner may always give one (AIOE's rule on `/act`). */
export const REFUSAL_ACTION = /reject|deny|decline|refuse|dismiss/i;
/** A level from anything (a record from an older host, a params block): undefined unless it is 1–5. */
export function riskLevelOf(v) {
    return typeof v === "number" && RISK_LEVELS.includes(v) ? v : undefined;
}
/** "Risk level 4 out of 5: needs approval — this deletes a branch." */
export function riskSentence(a) {
    const reason = a.riskReason.trim().replace(/[.\s]+$/, "");
    return `${riskLevelSentence(a.riskLevel)} — ${reason}.`;
}
/** The sentence, followed by who must approve when that is an owner. */
export function riskLines(a) {
    return [riskSentence(a), ...(ownerMustApprove(a.riskLevel) ? [OWNER_REQUIRED_SENTENCE] : [])];
}
/** The tone a surface colours the words with. */
export function riskTone(level) {
    return level >= 4 ? "danger" : level === 3 ? "warning" : "muted";
}
//# sourceMappingURL=riskWords.js.map